import { Footer } from '../components/Footer';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { trackView, trackConversion } from '@/lib/analytics';
import { calcAgeNum } from '@/lib/helpers';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Users, CheckCircle2, ChevronLeft, ChevronRight, User, UsersRound, Phone, MoreHorizontal, ClipboardCheck, RefreshCw, ShieldCheck, Loader2, UserPlus, Link2Off, Copy, Check } from 'lucide-react';
import AddressAutocompleteInput from '@/components/AddressAutocompleteInput';
import { emptyAddressFields, parseGooglePlaceAddress } from '@/lib/address';
import { formatBrazilDate } from '@/lib/timezone';

const API = `${window.location.origin}/api`;
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const SMS_COOLDOWN = 180; // 3 minutes

function collectPublicRegistrationDeviceInfo() {
  const nav = navigator || {};
  const connection = nav.connection || nav.mozConnection || nav.webkitConnection || {};
  return {
    user_agent: nav.userAgent || '',
    platform: nav.platform || '',
    language: nav.language || '',
    languages: Array.isArray(nav.languages) ? nav.languages.slice(0, 10) : [],
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    screen: {
      width: window.screen?.width,
      height: window.screen?.height,
      avail_width: window.screen?.availWidth,
      avail_height: window.screen?.availHeight,
      color_depth: window.screen?.colorDepth,
      pixel_depth: window.screen?.pixelDepth,
    },
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
      outer_width: window.outerWidth,
      outer_height: window.outerHeight,
      device_pixel_ratio: window.devicePixelRatio,
      orientation: window.screen?.orientation?.type || '',
    },
    connection: {
      effective_type: connection.effectiveType,
      downlink: connection.downlink,
      rtt: connection.rtt,
      save_data: connection.saveData,
    },
    memory: {
      device_memory: nav.deviceMemory,
      hardware_concurrency: nav.hardwareConcurrency,
    },
    device: {
      max_touch_points: nav.maxTouchPoints,
      cookie_enabled: nav.cookieEnabled,
      online: nav.onLine,
      standalone: window.matchMedia?.('(display-mode: standalone)')?.matches || false,
    },
    history_length: window.history?.length,
    referrer: document.referrer || '',
    dom_guard: window.__lastDomMutationGuard || null,
  };
}

async function sendPublicRegistrationDiagnostic(payload) {
  try {
    const body = JSON.stringify({ ...collectPublicRegistrationDeviceInfo(), ...payload });
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      const sent = navigator.sendBeacon(`${API}/public/registration-diagnostics`, blob);
      if (sent) return null;
    }
    const response = await fetch(`${API}/public/registration-diagnostics`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    });
    return response.ok ? response.json() : null;
  } catch (error) {
    console.warn('Falha ao enviar diagnóstico público', error);
    return null;
  }
}

function logPublicRegistrationEvent(payload) {
  sendPublicRegistrationDiagnostic({
    url: window.location.href,
    error_name: payload.error_name || 'PublicRegistrationEvent',
    error_message: payload.error_message || payload.source || 'Evento público',
    stack: '',
    ...payload,
  });
}

const FONT_MAP = {
  'Inter': "'Inter', sans-serif",
  'Georgia': "'Georgia', serif",
  'Playfair Display': "'Playfair Display', serif",
  'Roboto Mono': "'Roboto Mono', monospace",
  'Poppins': "'Poppins', sans-serif",
};

const BG_PRESETS = {
  lavender_mist: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 50%, #f0e7fe 100%)',
  rose_quartz: 'linear-gradient(135deg, #fff1f2 0%, #fce7f3 50%, #fdf2f8 100%)',
  morning_sky: 'linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 50%, #f0f9ff 100%)',
  sage_garden: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 50%, #f0fdf9 100%)',
  warm_sand: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 40%, #fff7ed 100%)',
  pearl: 'linear-gradient(180deg, #fafafa 0%, #f5f5f4 50%, #fafaf9 100%)',
  soft_lilac: 'linear-gradient(180deg, #faf5ff 0%, #f3e8ff 60%, #faf5ff 100%)',
  ocean_breeze: 'linear-gradient(135deg, #ecfeff 0%, #e0f2fe 50%, #eff6ff 100%)',
  blush_peach: 'linear-gradient(135deg, #fff7ed 0%, #fff1f2 50%, #fef2f2 100%)',
  mint_cream: 'linear-gradient(180deg, #f0fdfa 0%, #ecfdf5 50%, #f0fdf4 100%)',
  twilight: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #3730a3 100%)',
  midnight: 'linear-gradient(180deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
};

function getPageBgStyle(page) {
  if (!page) return { background: 'linear-gradient(to bottom right, #f8fafc, #eff6ff)' };
  const bgType = page.bg_type || 'solid';
  if (bgType === 'gradient') {
    const dir = page.bg_gradient_direction || 'to bottom';
    return { background: `linear-gradient(${dir}, ${page.cor_fundo || '#f8fafc'}, ${page.cor_fundo_2 || '#e2e8f0'})` };
  }
  if (bgType === 'preset' && page.bg_preset && BG_PRESETS[page.bg_preset]) {
    return { background: BG_PRESETS[page.bg_preset] };
  }
  return { backgroundColor: page.cor_fundo || '#f8fafc' };
}

const INSTRUMENTOS = [
  'Acordeon', 'Clarinete', 'Clarinete Alto', 'Clarinete Baixo', 'Corne Inglês',
  'Eufonio', 'Fagote', 'Flauta', 'Flugelhorn', 'Oboé', 'Piano',
  'Sax Alto', 'Sax Barítono', 'Sax Soprano', 'Sax Tenor',
  'Trombone', 'Trompa', 'Trompete', 'Tuba',
  'Viola', 'Violão', 'Violino', 'Violoncelo'
];

const WIZARD_STEPS = [
  { label: 'Pessoais', icon: User },
  { label: 'Família', icon: UsersRound },
  { label: 'Contato', icon: Phone },
  { label: 'Outros', icon: MoreHorizontal },
  { label: 'Resumo', icon: ClipboardCheck },
];

function RegistrationErrorFallback({ error, info, diagnosticId, onReset }) {
  const details = [
    `Mensagem: ${error?.name || 'Error'}: ${error?.message || 'Erro desconhecido'}`,
    diagnosticId ? `Diagnóstico: ${diagnosticId}` : '',
    `URL: ${window.location.href}`,
    `Horário: ${new Date().toISOString()}`,
    `Navegador: ${navigator.userAgent}`,
    info?.componentStack ? `Componente:${info.componentStack}` : '',
    error?.stack ? `Stack:\n${error.stack}` : '',
  ].filter(Boolean).join('\n\n');
  const copyDetails = async () => {
    try {
      await navigator.clipboard.writeText(details);
      toast.success('Detalhes do erro copiados');
    } catch {
      toast.error('Não foi possível copiar os detalhes');
    }
  };
  return <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4"><Card className="w-full max-w-md border-0 shadow-xl"><CardContent className="p-6 text-center space-y-4"><div className="w-14 h-14 rounded-full bg-red-50 mx-auto flex items-center justify-center"><RefreshCw className="w-6 h-6 text-red-600" /></div><div><h2 className="text-lg font-bold text-slate-900">Não foi possível continuar</h2><p className="mt-1 text-sm text-slate-500">A página encontrou uma falha. Os detalhes técnicos aparecem abaixo para facilitar a correção.</p></div><div className="rounded-xl border border-red-100 bg-red-50 p-3 text-left" data-testid="registration-error-details"><p className="text-xs font-bold uppercase tracking-wide text-red-700">Erro técnico</p><p className="mt-1 break-words text-sm font-semibold text-red-900" data-testid="registration-error-message">{error?.name || 'Error'}: {error?.message || 'Erro desconhecido'}</p>{diagnosticId && <p className="mt-2 rounded-lg bg-white px-2 py-1 text-xs font-semibold text-slate-600" data-testid="registration-error-diagnostic-id">ID: {diagnosticId}</p>}<details className="mt-2"><summary className="cursor-pointer text-xs font-semibold text-red-700" data-testid="registration-error-details-toggle">Ver detalhes completos</summary><pre className="mt-2 max-h-52 overflow-auto whitespace-pre-wrap rounded-lg bg-white p-2 text-[11px] leading-relaxed text-slate-700" data-testid="registration-error-stack">{details}</pre></details></div><div className="grid grid-cols-1 gap-2 sm:grid-cols-2"><Button onClick={copyDetails} variant="outline" className="w-full" data-testid="registration-error-copy-button"><Copy className="mr-2 h-4 w-4" />Copiar erro</Button><Button onClick={onReset} className="w-full" data-testid="registration-error-reload-button">Recarregar cadastro</Button></div></CardContent></Card></div>;
}

class RegistrationErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null, info: null, diagnosticId: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) {
    console.error('PublicRegistration crash', error, info);
    this.setState({ error, info });
    sendPublicRegistrationDiagnostic({
      slug: this.props.slug || window.__publicRegistrationContext?.slug,
      url: window.location.href,
      error_name: error?.name || 'Error',
      error_message: error?.message || 'Erro desconhecido',
      stack: error?.stack || '',
      component_stack: info?.componentStack || '',
      source: 'react_error_boundary',
      ...(window.__publicRegistrationContext || {}),
    }).then(result => {
      if (result?.diagnostic_id) this.setState({ diagnosticId: result.diagnostic_id });
    });
  }
  render() { if (this.state.hasError) return <RegistrationErrorFallback error={this.state.error} info={this.state.info} diagnosticId={this.state.diagnosticId} onReset={() => window.location.reload()} />; return this.props.children; }
}

function formatPhone(value) {
  const digits = toText(value).replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 2) return digits.length ? `(${digits}` : '';
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
}

const EMPTY_FORM = {
  nome_completo: '', genero: '', data_nascimento: '', email: '', whatsapp: '',
  whatsapp_pai: '', whatsapp_mae: '',
  tem_whatsapp_proprio: null, tem_email_proprio: null,
  batizado: false, selado: false, nome_pai: '', nome_mae: '',
  ...emptyAddressFields(),
  auxiliar_jovens: false, grupo_visita: false,
  organista: false, instrutora_musicalizacao: false, integrante_ebi: false,
  musico: false, instrumentos: [], instrutor_gem: false, aluno_gem: false
};

const toText = (value) => value === null || value === undefined ? '' : String(value);
const toBool = (value) => value === true || value === 'true' || value === '1' || value === 'sim' || value === 'Sim';
const toArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  if (typeof value === 'string') return value.split(',').map(item => item.trim()).filter(Boolean);
  return [];
};
const safeTrim = (value) => toText(value).trim();
const safeDigits = (value) => toText(value).replace(/\D/g, '');
const safeForm = (patch = {}) => ({
  ...EMPTY_FORM,
  ...patch,
  nome_completo: toText(patch.nome_completo),
  genero: toText(patch.genero),
  data_nascimento: toText(patch.data_nascimento),
  email: toText(patch.email),
  whatsapp: toText(patch.whatsapp),
  whatsapp_pai: toText(patch.whatsapp_pai),
  whatsapp_mae: toText(patch.whatsapp_mae),
  endereco: toText(patch.endereco || patch.endereco_formatado),
  endereco_formatado: toText(patch.endereco_formatado || patch.endereco),
  logradouro: toText(patch.logradouro),
  numero: toText(patch.numero),
  bairro: toText(patch.bairro),
  cidade: toText(patch.cidade),
  uf: toText(patch.uf),
  cep: toText(patch.cep),
  complemento: toText(patch.complemento),
  lat: patch.lat ?? null,
  lng: patch.lng ?? null,
  google_place_id: toText(patch.google_place_id),
  nome_pai: toText(patch.nome_pai),
  nome_mae: toText(patch.nome_mae),
  batizado: toBool(patch.batizado),
  selado: toBool(patch.selado),
  auxiliar_jovens: toBool(patch.auxiliar_jovens),
  grupo_visita: toBool(patch.grupo_visita),
  organista: toBool(patch.organista),
  instrutora_musicalizacao: toBool(patch.instrutora_musicalizacao),
  integrante_ebi: toBool(patch.integrante_ebi),
  musico: toBool(patch.musico),
  instrutor_gem: toBool(patch.instrutor_gem),
  aluno_gem: toBool(patch.aluno_gem),
  instrumentos: toArray(patch.instrumentos),
});

function normalizePersonMatch(item = {}, index = 0) {
  const normalized = safeForm(item || {});
  return {
    ...normalized,
    pessoa_id: item?.pessoa_id || `match-${index}`,
  };
}

function getErrorMessage(data, fallback = 'Erro ao processar solicitação') {
  const detail = data?.detail ?? data?.message;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(item => item?.msg || item?.message || JSON.stringify(item)).join('; ') || fallback;
  if (typeof detail === 'object') return detail.message || detail.msg || fallback;
  return String(detail);
}

function calcAge(dateStr) {
  return calcAgeNum(dateStr);
}

function getParticipantLabel(age) {
  if (age === null) return { nome: 'jovem ou criança', artigo: 'o(a)', do: 'do(a)' };
  if (age >= 12) return { nome: 'jovem', artigo: 'o', do: 'do' };
  return { nome: 'criança', artigo: 'a', do: 'da' };
}

function ProgressBar({ step, accentColor, accentTextColor }) {
  return (
    <div className="px-1 pt-2 pb-4" data-testid="progress-bar">
      <div className="flex items-center justify-between mb-3">
        {WIZARD_STEPS.map((s, i) => {
          const Icon = s.icon;
          const done = i < step;
          const active = i === step;
          return (
            <div key={i} className="flex flex-col items-center flex-1">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300 ${!done && !active ? 'bg-slate-100 text-slate-400' : ''}`}
                style={done || active ? { backgroundColor: accentColor || '#2563eb', color: accentTextColor || '#fff', ...(active ? { boxShadow: `0 0 0 4px ${(accentColor || '#2563eb') + '22'}` } : {}) } : undefined}>
                {done ? <CheckCircle2 className="w-4 h-4" /> : <Icon className="w-3.5 h-3.5" />}
              </div>
              <span className={`text-[9px] mt-1 text-center leading-tight ${active ? 'font-semibold' : 'text-slate-400'}`} style={active ? { color: accentColor || '#2563eb' } : undefined}>{s.label}</span>
            </div>
          );
        })}
      </div>
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500 ease-out" style={{ width: `${((step + 1) / WIZARD_STEPS.length) * 100}%`, backgroundColor: accentColor || '#2563eb' }} />
      </div>
    </div>
  );
}

function OtpInput({ value, onChange, length = 6 }) {
  const inputs = useRef([]);
  const handleChange = (idx, e) => {
    const val = e.target.value.replace(/\D/g, '');
    if (!val) return;
    const digits = val.split('');
    const newCode = value.split('');
    digits.forEach((d, i) => { if (idx + i < length) newCode[idx + i] = d; });
    const result = newCode.join('').slice(0, length);
    onChange(result);
    const nextIdx = Math.min(idx + digits.length, length - 1);
    inputs.current[nextIdx]?.focus();
  };
  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const newCode = value.split('');
      if (newCode[idx]) { newCode[idx] = ''; onChange(newCode.join('')); }
      else if (idx > 0) { newCode[idx - 1] = ''; onChange(newCode.join('')); inputs.current[idx - 1]?.focus(); }
    } else if (e.key === 'ArrowLeft' && idx > 0) { inputs.current[idx - 1]?.focus(); }
    else if (e.key === 'ArrowRight' && idx < length - 1) { inputs.current[idx + 1]?.focus(); }
  };
  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    onChange(pasted);
    inputs.current[Math.min(pasted.length, length - 1)]?.focus();
  };
  return (
    <div className="flex justify-center gap-2 sm:gap-3" data-testid="otp-input-group">
      {Array.from({ length }).map((_, i) => (
        <input key={i} ref={el => inputs.current[i] = el} type="text" inputMode="numeric" maxLength={1}
          value={value[i] || ''} onChange={(e) => handleChange(i, e)} onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste} onFocus={(e) => e.target.select()} data-testid={`otp-digit-${i}`}
          className="w-11 h-14 sm:w-12 sm:h-16 text-center text-xl sm:text-2xl font-bold rounded-xl border-2 border-slate-200 focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none transition-all bg-white text-slate-800" />
      ))}
    </div>
  );
}

function SummaryRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex justify-between py-1.5 border-b border-slate-100 last:border-b-0">
      <span className="text-sm text-slate-500">{label}</span>
      <span className="text-sm font-medium text-slate-800 text-right max-w-[60%]">{value}</span>
    </div>
  );
}

function PublicRegistrationInner() {
  const { slug } = useParams();
  const cardRef = useRef(null);

  const [pageLoading, setPageLoading] = useState(true);
  const [invalidSlug, setInvalidSlug] = useState(false);
  const [settings, setSettings] = useState({ app_name: 'CCBRJM', subtitle: '', terms_of_use: '' });
  const [churchSlug, setChurchSlug] = useState(slug || null);
  const [pageStyle, setPageStyle] = useState(null);

  // Flow: 'whatsapp' | 'select' | 'verify' | 'wizard'
  const [phase, setPhase] = useState('whatsapp');
  const [mode, setMode] = useState(null); // 'new' | 'update'
  const [verifiedPessoaId, setVerifiedPessoaId] = useState(null);

  // WhatsApp + matches
  const [inputWhatsapp, setInputWhatsapp] = useState('');
  const [checking, setChecking] = useState(false);
  const [matches, setMatches] = useState([]);

  // SMS state
  const [smsCode, setSmsCode] = useState('');
  const [smsSending, setSmsSending] = useState(false);
  const [smsVerifying, setSmsVerifying] = useState(false);
  const [maskedPhone, setMaskedPhone] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const cooldownRef = useRef(null);

  // Wizard state
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [copyDone, setCopyDone] = useState(false);

  const handleCopyFields = () => {
    const appName = settings.app_name || 'Igreja';
    const link = `${window.location.origin}/pessoas/${slug}`;
    const text = `📋 Cadastro - ${appName}\n\nPara se cadastrar, preencha os dados abaixo:\n\n👤 Nome Completo:\n📅 Data de Nascimento:\n👧 Gênero: ( ) Masculino ( ) Feminino\n📧 E-mail:\n📱 WhatsApp:\n👨 Nome do Pai:\n📱 WhatsApp do Pai:\n👩 Nome da Mãe:\n📱 WhatsApp da Mãe:\n✅ Batizado: ( ) Sim ( ) Não\n✅ Selado: ( ) Sim ( ) Não\n🤝 Auxiliar de Jovens: ( ) Sim ( ) Não\n🎹 Organista: ( ) Sim ( ) Não\n👩‍🏫 Instrutora de Musicalização: ( ) Sim ( ) Não\n👶 Integrante EBI: ( ) Sim ( ) Não\n🎵 Músico: ( ) Sim ( ) Não\n🎸 Instrumentos:\n🎓 Instrutor GEM: ( ) Sim ( ) Não\n📚 Aluno GEM: ( ) Sim ( ) Não`;
    navigator.clipboard.writeText(text);
    setCopyDone(true);
    toast.success('Campos copiados!');
    setTimeout(() => setCopyDone(false), 2000);
  };
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState(EMPTY_FORM);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  useEffect(() => {
    window.__publicRegistrationContext = { slug, church_slug: churchSlug, phase, step, mode };
  }, [slug, churchSlug, phase, step, mode]);

  useEffect(() => {
    logPublicRegistrationEvent({
      slug,
      phase,
      step,
      mode,
      source: 'public_registration_phase_change',
      error_message: `phase=${phase};step=${step};mode=${mode || 'none'}`,
    });
  }, [slug, phase, step, mode]);

  useEffect(() => {
    const sendRuntimeError = (payload) => {
      sendPublicRegistrationDiagnostic({
        slug,
        url: window.location.href,
        source: payload.source,
        error_name: payload.error_name || 'RuntimeError',
        error_message: payload.error_message || 'Erro runtime desconhecido',
        stack: payload.stack || '',
        ...(window.__publicRegistrationContext || {}),
      });
    };
    const onError = (event) => {
      sendRuntimeError({
        source: 'window_error',
        error_name: event.error?.name || 'WindowError',
        error_message: event.error?.message || event.message,
        stack: event.error?.stack || `${event.filename || ''}:${event.lineno || ''}:${event.colno || ''}`,
      });
    };
    const onUnhandledRejection = (event) => {
      const reason = event.reason;
      sendRuntimeError({
        source: 'unhandled_rejection',
        error_name: reason?.name || 'UnhandledRejection',
        error_message: reason?.message || String(reason || 'Promise rejeitada sem mensagem'),
        stack: reason?.stack || '',
      });
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }, [slug]);

  const scrollToCardTop = useCallback(() => {
    requestAnimationFrame(() => {
      cardRef.current?.scrollIntoView({ block: 'start', behavior: 'auto' });
    });
  }, []);

  const age = calcAge(form.data_nascimento);
  const participant = getParticipantLabel(age);
  const isOver14 = age !== null && age >= 14;

  useEffect(() => {
    const fetchChurchInfo = async () => {
      setPageLoading(true);
      try {
        if (slug) {
          const r = await fetch(`${API}/churches/${slug}/public`);
          if (!r.ok) { setInvalidSlug(true); return; }
          const data = await r.json();
          setSettings({ app_name: data.app_name, subtitle: data.subtitle, terms_of_use: data.terms_of_use });
          setChurchSlug(data.slug);
          if (data.page_style) setPageStyle(data.page_style);
          document.title = 'CCBRJM';
          trackView('cadastro', slug, '');
        } else {
          const r = await fetch(`${API}/settings`);
          const data = r.ok ? await r.json() : {};
          if (data.app_name) { setSettings(prev => ({ ...prev, ...data })); document.title = 'CCBRJM'; }
          if (data.slug_cadastro || data.slug) setChurchSlug(data.slug_cadastro || data.slug);
        }
      } catch { setInvalidSlug(true); }
      finally { setPageLoading(false); }
    };
    fetchChurchInfo();
  }, [slug, scrollToCardTop]);

  useEffect(() => {
    if (cooldown > 0) {
      cooldownRef.current = setTimeout(() => setCooldown(c => c - 1), 1000);
      return () => clearTimeout(cooldownRef.current);
    }
  }, [cooldown]);

  // --- Check WhatsApp ---
  const handleWhatsappSubmit = async () => {
    const digits = safeDigits(inputWhatsapp);
    if (digits.length < 10) { toast.error('Informe um número de WhatsApp válido (com DDD)'); return; }
    setChecking(true);
    try {
      if (!churchSlug) {
        toast.error('Link de cadastro não carregado. Recarregue a página e tente novamente.');
        return;
      }
      const r = await fetch(`${API}/public/check-whatsapp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ whatsapp: inputWhatsapp, church_slug: churchSlug }),
      });
      if (!r.ok) {
        let msg = 'Erro ao verificar número';
        try { const err = await r.json(); msg = getErrorMessage(err, msg); } catch { msg = 'Erro ao verificar número'; }
        logPublicRegistrationEvent({ slug, church_slug: churchSlug, phase, step, mode, source: 'check_whatsapp_api_error', error_name: `HTTP_${r.status}`, error_message: msg });
        toast.error(msg); return;
      }
      let data;
      try { data = await r.json(); } catch (error) { logPublicRegistrationEvent({ slug, church_slug: churchSlug, phase, step, mode, source: 'check_whatsapp_json_error', error_name: error?.name, error_message: error?.message }); toast.error('Erro ao processar resposta.'); return; }

      if (data?.exists) {
        if (data.already_verified && Array.isArray(data.matches) && data.matches.length) {
          // Recently verified — skip SMS, go straight to selection
          setMode('update');
          setMatches((data.matches || []).map(normalizePersonMatch));
          setPhase('select');
          setTimeout(scrollToCardTop, 0);
          toast.success('Número já verificado recentemente.');
        } else {
          // Existing — send SMS for verification (names shown only after SMS)
          setMode('update');
          await sendSmsCode();
        }
      } else {
        // New — go to registration wizard
        setMode('new');
        setVerifiedPessoaId(null);
        setMatches([]);
        setSmsCode('');
        setErrors({});
        setAcceptedTerms(false);
        setStep(0);
        setForm(safeForm({ whatsapp: inputWhatsapp }));
        setPhase('wizard');
        setTimeout(scrollToCardTop, 0);
      }
    } catch (err) { logPublicRegistrationEvent({ slug, church_slug: churchSlug, phase, step, mode, source: 'check_whatsapp_unexpected_error', error_name: err?.name, error_message: err?.message, stack: err?.stack }); toast.error(`Erro ao verificar número: ${err?.message || 'falha inesperada'}`); }
    finally { setChecking(false); }
  };

  // --- Select person (after SMS verified) ---
  const handleSelectPerson = (pessoa) => {
    setVerifiedPessoaId(pessoa.pessoa_id);
    prefillForm(pessoa);
    setPhase('wizard');
    setTimeout(scrollToCardTop, 0);
  };

  const handleNewFromSelect = () => {
    setMode('new');
    setForm(safeForm({ whatsapp: inputWhatsapp }));
    setPhase('wizard');
    setTimeout(scrollToCardTop, 0);
  };

  // --- SMS ---
  const sendSmsCode = useCallback(async () => {
    setSmsSending(true);
    try {
      const r = await fetch(`${API}/public/send-code`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ whatsapp: inputWhatsapp, church_slug: churchSlug }),
      });
      if (r.status === 429) {
        // Rate limited — code already sent, go to verify with remaining cooldown
        let remaining = SMS_COOLDOWN;
        let masked = inputWhatsapp;
        try {
          const err = await r.json();
          if (err.detail?.remaining) remaining = err.detail.remaining;
          if (err.detail?.masked_phone) masked = err.detail.masked_phone;
          toast.error(err.detail?.message || 'Aguarde alguns minutos para tentar novamente.');
        } catch {
          toast.error('Código já enviado. Aguarde alguns minutos para reenviar.');
        }
        setMaskedPhone(masked);
        setCooldown(remaining);
        setPhase('verify');
        setTimeout(scrollToCardTop, 0);
        return;
      }
      if (!r.ok) {
        let msg = 'Erro ao enviar código';
        try { const err = await r.json(); msg = getErrorMessage(err, msg); } catch { msg = 'Erro ao enviar código'; }
        logPublicRegistrationEvent({ slug, church_slug: churchSlug, phase, step, mode, source: 'send_code_api_error', error_name: `HTTP_${r.status}`, error_message: msg });
        throw new Error(msg);
      }
      const data = await r.json();
      setMaskedPhone(data.masked_phone || inputWhatsapp);
      setCooldown(SMS_COOLDOWN);
      setPhase('verify');
      setTimeout(scrollToCardTop, 0);
      toast.success('Código enviado por SMS!');
    } catch (err) { logPublicRegistrationEvent({ slug, church_slug: churchSlug, phase, step, mode, source: 'send_code_unexpected_error', error_name: err?.name, error_message: err?.message, stack: err?.stack }); toast.error(err.message); }
    finally { setSmsSending(false); }
  }, [inputWhatsapp, churchSlug, scrollToCardTop, slug, phase, step, mode]);

  const handleVerifyCode = async () => {
    if (smsCode.length < 6) { toast.error('Informe o código de 6 dígitos'); return; }
    setSmsVerifying(true);
    try {
      const r = await fetch(`${API}/public/verify-code`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ whatsapp: inputWhatsapp, church_slug: churchSlug, code: smsCode }),
      });
      if (!r.ok) {
        let msg = 'Código inválido ou expirado. Verifique e tente novamente.';
        try { const err = await r.json(); msg = getErrorMessage(err, msg); } catch { msg = 'Código inválido ou expirado. Verifique e tente novamente.'; }
        logPublicRegistrationEvent({ slug, church_slug: churchSlug, phase, step, mode, source: 'verify_code_api_error', error_name: `HTTP_${r.status}`, error_message: msg });
        toast.error(msg);
        return;
      }
      let data;
      try { data = await r.json(); } catch (error) { logPublicRegistrationEvent({ slug, church_slug: churchSlug, phase, step, mode, source: 'verify_code_json_error', error_name: error?.name, error_message: error?.message }); toast.error('Erro ao processar resposta. Tente novamente.'); return; }

      setMatches((data.matches || []).map(normalizePersonMatch));
      setPhase('select');
      setTimeout(scrollToCardTop, 0);
      toast.success('Identidade verificada!');
    } catch (err) { logPublicRegistrationEvent({ slug, church_slug: churchSlug, phase, step, mode, source: 'verify_code_unexpected_error', error_name: err?.name, error_message: err?.message, stack: err?.stack }); toast.error(`Erro ao confirmar código: ${err?.message || 'falha inesperada'}`); }
    finally { setSmsVerifying(false); }
  };

  const prefillForm = (p) => {
    if (!p) return;
    setForm(safeForm({
      nome_completo: p.nome_completo || '',
      genero: p.genero || '',
      data_nascimento: p.data_nascimento || '',
      email: p.email || '',
      whatsapp: p.whatsapp || '',
      whatsapp_pai: p.whatsapp_pai || '',
      whatsapp_mae: p.whatsapp_mae || '',
      endereco: p.endereco || p.endereco_formatado || '',
      endereco_formatado: p.endereco_formatado || p.endereco || '',
      logradouro: p.logradouro || '',
      numero: p.numero || '',
      bairro: p.bairro || '',
      cidade: p.cidade || '',
      uf: p.uf || '',
      cep: p.cep || '',
      complemento: p.complemento || '',
      lat: p.lat ?? null,
      lng: p.lng ?? null,
      google_place_id: p.google_place_id || '',
      tem_whatsapp_proprio: p.whatsapp ? true : null,
      tem_email_proprio: p.email ? true : null,
      batizado: p.batizado || false,
      selado: p.selado || false,
      nome_pai: p.nome_pai || '',
      nome_mae: p.nome_mae || '',
      auxiliar_jovens: p.auxiliar_jovens || false,
      grupo_visita: p.grupo_visita || false,
      organista: p.organista || false,
      instrutora_musicalizacao: p.instrutora_musicalizacao || false,
      integrante_ebi: p.integrante_ebi || false,
      musico: p.musico || false,
      instrumentos: p.instrumentos || [],
      instrutor_gem: p.instrutor_gem || false,
      aluno_gem: p.aluno_gem || false,
    }));
  };

  // --- Wizard ---
  const toggleInstrumento = (inst) => setForm(p => ({
    ...p, instrumentos: toArray(p.instrumentos).includes(inst) ? toArray(p.instrumentos).filter(i => i !== inst) : [...toArray(p.instrumentos), inst]
  }));

  const validateStep = (s) => {
    const e = {};
    if (s === 0) {
      if (!safeTrim(form.nome_completo)) e.nome_completo = 'Obrigatório';
      if (!form.genero) e.genero = 'Obrigatório';
      if (!form.data_nascimento) e.data_nascimento = 'Obrigatório';
    } else if (s === 1) {
      if (!safeTrim(form.nome_pai)) e.nome_pai = 'Obrigatório';
      if (safeDigits(form.whatsapp_pai).length < 11) e.whatsapp_pai = 'WhatsApp obrigatório (11 dígitos)';
      if (!safeTrim(form.nome_mae)) e.nome_mae = 'Obrigatório';
      if (safeDigits(form.whatsapp_mae).length < 11) e.whatsapp_mae = 'WhatsApp obrigatório (11 dígitos)';
    } else if (s === 2) {
      if (isOver14) {
        if (safeDigits(form.whatsapp).length < 11) e.whatsapp = 'WhatsApp obrigatório para maiores de 14 anos';
        if (!form.email || !EMAIL_REGEX.test(form.email)) e.email = 'E-mail obrigatório para maiores de 14 anos';
      } else {
        if (form.tem_whatsapp_proprio === null) e.tem_whatsapp_proprio = 'Selecione uma opção';
        if (form.tem_whatsapp_proprio === true && safeDigits(form.whatsapp).length < 11) e.whatsapp = 'WhatsApp obrigatório (11 dígitos)';
        if (form.tem_email_proprio === null) e.tem_email_proprio = 'Selecione uma opção';
        if (form.tem_email_proprio === true && (!form.email || !EMAIL_REGEX.test(form.email))) e.email = 'Email obrigatório e válido';
      }
    } else if (s === 3) {
      if (form.musico && toArray(form.instrumentos).length === 0) e.instrumentos = 'Selecione ao menos um instrumento';
    } else if (s === 4) {
      if (settings.terms_of_use && !acceptedTerms) e.terms = 'Você deve aceitar o termo de uso';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const checkDuplicate = async () => {
    try {
      const formData = new FormData();
      formData.append('nome_completo', safeTrim(form.nome_completo));
      if (form.whatsapp) formData.append('whatsapp', form.whatsapp);
      if (form.email) formData.append('email', form.email);
      if (churchSlug) formData.append('church_slug', churchSlug);
      const r = await fetch(`${API}/public/check-duplicate`, { method: 'POST', body: formData });
      if (r.ok) { const data = await r.json(); if (data.duplicate) { toast.error(data.message, { duration: 6000 }); return true; } }
    } catch {
      return false;
    }
    return false;
  };

  const goNext = async () => {
    if (!validateStep(step)) { toast.error('Corrija os campos destacados'); return; }
    if (step === 2 && mode !== 'update') { if (await checkDuplicate()) return; }
    setErrors({}); setStep(s => Math.min(s + 1, WIZARD_STEPS.length - 1)); setTimeout(scrollToCardTop, 0);
  };

  const goBack = () => {
    if (step === 0) {
      if (mode === 'update' && matches.length > 0) {
        setPhase('select');
        setTimeout(scrollToCardTop, 0);
      } else {
        setPhase('whatsapp');
        setMode(null);
        setForm(safeForm());
        setTimeout(scrollToCardTop, 0);
      }
      return;
    }
    setErrors({}); setStep(s => Math.max(s - 1, 0)); setTimeout(scrollToCardTop, 0);
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) { toast.error('Corrija os campos destacados'); return; }
    setLoading(true);
    try {
      if (mode === 'update') {
        const body = {
          nome_completo: safeTrim(form.nome_completo), genero: form.genero, data_nascimento: form.data_nascimento,
          email: form.email || null, whatsapp: form.whatsapp || null,
          whatsapp_pai: form.whatsapp_pai || null, whatsapp_mae: form.whatsapp_mae || null,
          batizado: form.batizado, selado: form.selado,
          nome_pai: safeTrim(form.nome_pai), nome_mae: safeTrim(form.nome_mae),
          endereco: form.endereco || '', endereco_formatado: form.endereco_formatado || form.endereco || '', logradouro: form.logradouro || '', numero: form.numero || '', bairro: form.bairro || '', cidade: form.cidade || '', uf: form.uf || '', cep: form.cep || '', complemento: form.complemento || '', lat: form.lat, lng: form.lng, google_place_id: form.google_place_id || '',
          auxiliar_jovens: form.auxiliar_jovens, grupo_visita: form.grupo_visita,
          organista: form.organista, instrutora_musicalizacao: form.instrutora_musicalizacao,
          integrante_ebi: form.integrante_ebi, musico: form.musico, instrumentos: toArray(form.instrumentos),
          instrutor_gem: form.instrutor_gem, aluno_gem: form.aluno_gem, church_slug: churchSlug,
        };
        const r = await fetch(`${API}/public/update-registration/${verifiedPessoaId}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
        });
        if (!r.ok) { const err = await r.json(); throw new Error(getErrorMessage(err, 'Erro ao enviar atualização')); }
        setSubmitted(true); toast.success('Atualização enviada para aprovação!');
      } else {
        const formData = new FormData();
        formData.append('nome_completo', safeTrim(form.nome_completo));
        formData.append('genero', form.genero);
        formData.append('data_nascimento', form.data_nascimento);
        formData.append('batizado', String(form.batizado));
        formData.append('selado', String(form.selado));
        formData.append('nome_pai', safeTrim(form.nome_pai));
        formData.append('nome_mae', safeTrim(form.nome_mae));
        if (form.email) formData.append('email', form.email);
        if (form.whatsapp) formData.append('whatsapp', form.whatsapp);
        if (form.whatsapp_pai) formData.append('whatsapp_pai', form.whatsapp_pai);
        if (form.whatsapp_mae) formData.append('whatsapp_mae', form.whatsapp_mae);
        ['endereco', 'endereco_formatado', 'logradouro', 'numero', 'bairro', 'cidade', 'uf', 'cep', 'complemento', 'google_place_id'].forEach(key => formData.append(key, form[key] || ''));
        if (form.lat !== null && form.lat !== undefined) formData.append('lat', String(form.lat));
        if (form.lng !== null && form.lng !== undefined) formData.append('lng', String(form.lng));
        formData.append('auxiliar_jovens', String(form.auxiliar_jovens));
        formData.append('grupo_visita', String(form.grupo_visita));
        formData.append('organista', String(form.organista));
        formData.append('instrutora_musicalizacao', String(form.instrutora_musicalizacao));
        formData.append('integrante_ebi', String(form.integrante_ebi));
        formData.append('musico', String(form.musico));
        formData.append('instrumentos', toArray(form.instrumentos).join(','));
        formData.append('instrutor_gem', String(form.instrutor_gem));
        formData.append('aluno_gem', String(form.aluno_gem));
        if (churchSlug) formData.append('church_slug', churchSlug);
        const r = await fetch(`${API}/public/register-with-photo`, { method: 'POST', body: formData });
        if (!r.ok) { let msg = 'Erro ao enviar cadastro'; try { const err = await r.json(); msg = getErrorMessage(err, msg); } catch { msg = 'Erro ao enviar cadastro'; } throw new Error(msg); }
        setSubmitted(true); toast.success('Cadastro enviado com sucesso!'); trackConversion('cadastro', slug, '');
      }
    } catch (error) { toast.error(error.message); } finally { setLoading(false); }
  };

  const resetAll = () => {
    setSubmitted(false); setForm(safeForm()); setStep(0); setAcceptedTerms(false); setErrors({});
    setPhase('whatsapp'); setMode(null); setVerifiedPessoaId(null);
    setInputWhatsapp(''); setSmsCode(''); setMaskedPhone(''); setCooldown(0); setMatches([]);
  };

  // --- Style variables ---
  const bgStyle = getPageBgStyle(pageStyle);
  const fontFamily = pageStyle?.fonte ? (FONT_MAP[pageStyle.fonte] || FONT_MAP['Inter']) : undefined;
  const titleColor = pageStyle?.cor_fonte_nome || undefined;
  const bioColor = pageStyle?.cor_fonte_bio || undefined;
  const iconColor = pageStyle?.cor_icone || undefined;
  const iconBg = pageStyle?.cor_fundo_icone || undefined;

  const btnStyle = { backgroundColor: iconBg || '#2563eb', color: iconColor || '#ffffff' };

  // --- Render ---
  if (pageLoading) return (
    <div className="min-h-screen flex items-center justify-center p-4" style={bgStyle}>
      <div className="text-slate-500" data-testid="registration-loading">Carregando...</div>
    </div>
  );

  if (invalidSlug) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="text-center" data-testid="registration-invalid-slug">
        <div className="w-16 h-16 bg-slate-100 rounded-full mx-auto mb-4 flex items-center justify-center">
          <Link2Off className="w-8 h-8 text-slate-400" />
        </div>
        <h2 className="text-lg font-semibold text-slate-700 mb-1">Página não encontrada</h2>
        <p className="text-sm text-slate-500">O link que você acessou não existe ou foi desativado.</p>
      </div>
    </div>
  );

  if (submitted) return (
    <div className="min-h-screen flex items-center justify-center p-4" style={bgStyle}>
      <Card className="w-full max-w-md p-8 bg-white/90 backdrop-blur-sm shadow-xl border-0 text-center" data-testid="registration-success">
        <div className="w-20 h-20 bg-green-50 rounded-full mx-auto mb-6 flex items-center justify-center">
          <CheckCircle2 className="w-10 h-10 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold mb-3" style={{ fontFamily, color: titleColor || '#1e293b' }}>
          {mode === 'update' ? 'Atualização Enviada!' : 'Cadastro Enviado!'}
        </h2>
        <p className="text-slate-600 mb-6" style={{ color: bioColor || undefined }}>
          {mode === 'update'
            ? 'Sua atualização foi enviada para aprovação. O administrador irá analisar as alterações.'
            : 'Seu cadastro foi enviado para aprovação.'}
        </p>
        <Button onClick={resetAll} variant="outline" data-testid="register-another-btn">Voltar ao Início</Button>
      </Card>
    </div>
  );

  const boolLabel = (v) => v ? 'Sim' : 'Não';
  const formatCooldown = (s) => { const m = Math.floor(s / 60); const sec = s % 60; return `${m}:${String(sec).padStart(2, '0')}`; };

  return (
    <div className="notranslate min-h-screen flex items-start justify-center px-4 py-4 sm:py-8" translate="no" style={{ ...bgStyle, fontFamily }}>
      <div className="max-w-lg w-full">
        <Card ref={cardRef} className="bg-white/90 backdrop-blur-sm shadow-xl border-0 overflow-hidden relative scroll-mt-4" data-testid="registration-form-card">
          <button onClick={handleCopyFields} className="absolute top-4 right-4 p-2 rounded-lg hover:bg-slate-100 transition-colors z-10" title="Copiar campos do cadastro" data-testid="copy-fields-btn">
            {copyDone ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-slate-400" />}
          </button>
          <CardHeader className="text-center pb-2">
            <div className="w-14 h-14 rounded-2xl mx-auto mb-3 flex items-center justify-center shadow-lg" style={{ backgroundColor: iconBg || '#2563eb' }}>
              <Users className="w-7 h-7" style={{ color: iconColor || '#ffffff' }} />
            </div>
            <CardTitle className="text-2xl font-bold" style={{ fontFamily, color: titleColor || '#1e293b' }}>{settings.app_name}</CardTitle>
            <CardDescription style={{ color: bioColor || undefined }}>
              {phase === 'whatsapp' && 'Informe seu WhatsApp para iniciar'}
              {phase === 'select' && 'Cadastros encontrados'}
              {phase === 'verify' && 'Verificação de identidade'}
              {phase === 'wizard' && (mode === 'update' ? 'Atualize seus dados' : 'Cadastro de jovens e crianças')}
            </CardDescription>
          </CardHeader>

          <CardContent>
            {/* PHASE: WHATSAPP */}
            {phase === 'whatsapp' && (
              <div className="space-y-5 py-4" data-testid="phase-whatsapp">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                    style={{ backgroundColor: (iconBg || '#2563eb') + '18' }}>
                    <Phone className="w-7 h-7" style={{ color: iconBg || '#2563eb' }} />
                  </div>
                  <p className="text-sm text-slate-600">
                    Informe o número de WhatsApp do jovem, criança ou responsável.
                  </p>
                </div>
                <div>
                  <Label>WhatsApp</Label>
                  <Input value={inputWhatsapp} onChange={(e) => setInputWhatsapp(formatPhone(e.target.value))}
                    maxLength={15} placeholder="(00) 00000-0000" data-testid="input-whatsapp-initial"
                    onKeyDown={(e) => { if (e.key === 'Enter') handleWhatsappSubmit(); }} />
                </div>
                <Button onClick={handleWhatsappSubmit} disabled={checking || safeDigits(inputWhatsapp).length < 10}
                  data-testid="btn-continue-whatsapp" className="w-full" style={btnStyle}>
                  {checking ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Verificando...</> : 'Continuar'}
                </Button>
              </div>
            )}

            {/* PHASE: SELECT PERSON */}
            {phase === 'select' && (
              <div className="space-y-4 py-4" data-testid="phase-select">
                <p className="text-sm text-slate-600 text-center">
                  Encontramos {matches.length === 1 ? '1 cadastro' : `${matches.length} cadastros`} vinculados a este WhatsApp.
                  Selecione qual deseja atualizar:
                </p>

                <div className="space-y-4">
                  {matches.map(m => {
                    const mAge = calcAge(m.data_nascimento);
                    return (
                      <button key={m.pessoa_id} onClick={() => handleSelectPerson(m)}
                        data-testid={`select-person-${m.pessoa_id}`}
                        className="w-full p-4 rounded-xl border-2 transition-all duration-200 flex items-center gap-3 text-left group disabled:opacity-50"
                        style={{ borderColor: '#e2e8f0', marginBottom: '12px' }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = iconBg || '#60a5fa'}
                        onMouseLeave={e => e.currentTarget.style.borderColor = '#e2e8f0'}>
                        <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-colors"
                          style={{ backgroundColor: (iconBg || '#2563eb') + '18', color: iconBg || '#2563eb' }}>
                          <User className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-slate-800 truncate">{m.nome_completo}</p>
                          <p className="text-sm text-slate-500">{mAge !== null ? `${mAge} anos` : 'Idade não informada'}</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                      </button>
                    );
                  })}
                </div>

                <div className="border-t border-slate-200 pt-4 mt-4">
                  <button onClick={handleNewFromSelect} data-testid="btn-new-from-select"
                    className="w-full p-4 rounded-xl border-2 border-dashed transition-all duration-200 flex items-center gap-3 text-left group"
                    style={{ borderColor: '#cbd5e1' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = iconBg || '#60a5fa'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = '#cbd5e1'}>
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-colors"
                      style={{ backgroundColor: (iconBg || '#2563eb') + '18', color: iconBg || '#2563eb' }}>
                      <UserPlus className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-700">Novo Cadastro</p>
                      <p className="text-sm text-slate-500">Cadastrar outra pessoa com este WhatsApp</p>
                    </div>
                  </button>
                </div>

                <Button variant="outline" onClick={() => { setPhase('whatsapp'); setMatches([]); setMode(null); setSmsCode(''); }}
                  data-testid="btn-select-back" className="gap-1 mt-2">
                  <ChevronLeft className="w-4 h-4" /> Voltar
                </Button>
              </div>
            )}

            {/* PHASE: SMS VERIFY */}
            {phase === 'verify' && (
              <div className="space-y-6 py-4" data-testid="phase-verify">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                    style={{ backgroundColor: (iconBg || '#2563eb') + '18' }}>
                    <ShieldCheck className="w-7 h-7" style={{ color: iconBg || '#2563eb' }} />
                  </div>
                  <p className="text-sm text-slate-600">
                    Enviamos um código de 6 dígitos por SMS para <span className="font-semibold text-slate-800">{maskedPhone}</span>.
                  </p>
                </div>
                <OtpInput value={smsCode} onChange={setSmsCode} />
                <div className="text-center">
                  <button onClick={sendSmsCode} disabled={cooldown > 0 || smsSending}
                    className="text-sm hover:opacity-80 disabled:text-slate-400 disabled:cursor-not-allowed inline-flex items-center gap-1"
                    style={{ color: iconColor || '#2563eb' }}
                    data-testid="btn-resend-code">
                    <RefreshCw className="w-3.5 h-3.5" />
                    {cooldown > 0 ? `Reenviar em ${formatCooldown(cooldown)}` : 'Reenviar código'}
                  </button>
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => { setPhase('whatsapp'); setMode(null); setSmsCode(''); }}
                    data-testid="btn-verify-back" className="gap-1">
                    <ChevronLeft className="w-4 h-4" /> Voltar
                  </Button>
                  <Button onClick={handleVerifyCode} disabled={smsVerifying || smsCode.length < 6}
                    data-testid="btn-verify-code" className="flex-1" style={btnStyle}>
                    {smsVerifying ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Verificando...</> : 'Confirmar'}
                  </Button>
                </div>
              </div>
            )}

            {/* PHASE: WIZARD */}
            {phase === 'wizard' && (
              <>
                {mode === 'update' && (
                  <div className="mb-3 px-3 py-2 rounded-lg" style={{ backgroundColor: (iconBg || '#2563eb') + '12', border: `1px solid ${(iconBg || '#2563eb') + '33'}` }}>
                    <p className="text-xs font-medium text-center" style={{ color: iconBg || '#2563eb' }}>Modo Atualização — Edite os campos que deseja alterar</p>
                  </div>
                )}
                <ProgressBar step={step} accentColor={iconBg} accentTextColor={iconColor} />
                <div key={step}>

                  {step === 0 && (
                    <div className="space-y-4" data-testid="step-0">
                      <p className="text-sm text-slate-500 mb-2">Informe os dados pessoais {participant.do} {participant.nome}.</p>
                      <div>
                        <Label>Nome Completo *</Label>
                        <Input value={form.nome_completo} onChange={(e) => setForm({ ...form, nome_completo: e.target.value })} data-testid="input-nome" className={errors.nome_completo ? 'border-red-500' : ''} />
                        {errors.nome_completo && <p className="text-xs text-red-500 mt-1">{errors.nome_completo}</p>}
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Gênero *</Label>
                          <Select value={form.genero} onValueChange={(v) => setForm(prev => ({ ...prev, genero: v, organista: false, instrutora_musicalizacao: false, integrante_ebi: false, musico: false, instrumentos: [], instrutor_gem: false, aluno_gem: false }))}>
                            <SelectTrigger data-testid="select-genero" className={errors.genero ? 'border-red-500' : ''}><SelectValue placeholder="Selecione" /></SelectTrigger>
                            <SelectContent><SelectItem value="masculino">Masculino</SelectItem><SelectItem value="feminino">Feminino</SelectItem></SelectContent>
                          </Select>
                          {errors.genero && <p className="text-xs text-red-500 mt-1">{errors.genero}</p>}
                        </div>
                        <div>
                          <Label>Data de Nascimento *</Label>
                          <Input type="date" value={form.data_nascimento} onChange={(e) => setForm({ ...form, data_nascimento: e.target.value })} data-testid="input-dob" className={errors.data_nascimento ? 'border-red-500' : ''} />
                          {errors.data_nascimento && <p className="text-xs text-red-500 mt-1">{errors.data_nascimento}</p>}
                        </div>
                      </div>
                    </div>
                  )}

                  {step === 1 && (
                    <div className="space-y-4" data-testid="step-1">
                      <p className="text-sm text-slate-500 mb-2">Informe os dados dos pais ou responsáveis.</p>
                      <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
                        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Pai</p>
                        <div className="grid grid-cols-1 sm:grid-cols-[3fr_2fr] gap-3">
                          <div><Label>Nome completo *</Label><Input value={form.nome_pai} onChange={(e) => setForm({ ...form, nome_pai: e.target.value })} data-testid="input-pai" className={errors.nome_pai ? 'border-red-500' : ''} />{errors.nome_pai && <p className="text-xs text-red-500 mt-1">{errors.nome_pai}</p>}</div>
                          <div><Label>WhatsApp *</Label><Input value={form.whatsapp_pai} onChange={(e) => setForm({ ...form, whatsapp_pai: formatPhone(e.target.value) })} data-testid="input-whatsapp-pai" className={errors.whatsapp_pai ? 'border-red-500' : ''} />{errors.whatsapp_pai && <p className="text-xs text-red-500 mt-1">{errors.whatsapp_pai}</p>}</div>
                        </div>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
                        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Mãe</p>
                        <div className="grid grid-cols-1 sm:grid-cols-[3fr_2fr] gap-3">
                          <div><Label>Nome completo *</Label><Input value={form.nome_mae} onChange={(e) => setForm({ ...form, nome_mae: e.target.value })} data-testid="input-mae" className={errors.nome_mae ? 'border-red-500' : ''} />{errors.nome_mae && <p className="text-xs text-red-500 mt-1">{errors.nome_mae}</p>}</div>
                          <div><Label>WhatsApp *</Label><Input value={form.whatsapp_mae} onChange={(e) => setForm({ ...form, whatsapp_mae: formatPhone(e.target.value) })} data-testid="input-whatsapp-mae" className={errors.whatsapp_mae ? 'border-red-500' : ''} />{errors.whatsapp_mae && <p className="text-xs text-red-500 mt-1">{errors.whatsapp_mae}</p>}</div>
                        </div>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3" data-testid="public-registration-address-section">
                        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Endereço</p>
                        <div className="grid grid-cols-1 gap-3">
                          <div><Label>Endereço</Label><AddressAutocompleteInput value={form.endereco_formatado || form.endereco || ''} onChange={(value, place) => setForm(prev => ({ ...prev, ...(place ? parseGooglePlaceAddress(place, value) : { endereco: value, endereco_formatado: value, lat: null, lng: null, google_place_id: '' }) }))} placeholder="Buscar endereço..." testId="public-registration-address-input" /></div>
                          <div><Label>Complemento</Label><Input value={form.complemento || ''} onChange={(e) => setForm({ ...form, complemento: e.target.value })} placeholder="Casa, bloco, apto..." data-testid="public-registration-address-complement-input" /></div>
                        </div>
                      </div>
                    </div>
                  )}

                  {step === 2 && (
                    <div className="space-y-5" data-testid="step-2">
                      <p className="text-sm text-slate-500 mb-2">Informações de contato {participant.do} próprio {participant.nome}.</p>
                      {isOver14 ? (
                        <>
                          {inputWhatsapp && form.whatsapp === inputWhatsapp ? (
                            <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 flex items-center gap-3" style={{marginBottom: '0.75rem'}}>
                              <Phone className="w-4 h-4 text-blue-600 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-xs text-slate-400">WhatsApp</p>
                                <p className="text-sm font-medium text-slate-700">{form.whatsapp}</p>
                              </div>
                            </div>
                          ) : (
                            <div><Label>WhatsApp *</Label><Input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: formatPhone(e.target.value) })} placeholder="(00) 00000-0000" data-testid="input-whatsapp" className={errors.whatsapp ? 'border-red-500' : ''} />{errors.whatsapp && <p className="text-xs text-red-500 mt-1">{errors.whatsapp}</p>}</div>
                          )}
                          <div className="mt-3"><Label>E-mail *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@exemplo.com" data-testid="input-email" className={errors.email ? 'border-red-500' : ''} />{errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}</div>
                        </>
                      ) : (
                        <>
                          <div className="space-y-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-sm font-semibold text-slate-700">{`${participant.artigo.charAt(0).toUpperCase()}${participant.artigo.slice(1)}`} {participant.nome} possui WhatsApp próprio?</p>
                              <div className="flex gap-2 flex-shrink-0">
                                <Button type="button" variant={form.tem_whatsapp_proprio === true ? 'default' : 'outline'} size="sm" onClick={() => setForm({ ...form, tem_whatsapp_proprio: true })} data-testid="btn-whatsapp-sim" style={form.tem_whatsapp_proprio === true ? btnStyle : undefined}>Sim</Button>
                                <Button type="button" variant={form.tem_whatsapp_proprio === false ? 'default' : 'outline'} size="sm" onClick={() => setForm({ ...form, tem_whatsapp_proprio: false, whatsapp: '' })} data-testid="btn-whatsapp-nao" style={form.tem_whatsapp_proprio === false ? btnStyle : undefined}>Não</Button>
                              </div>
                            </div>
                            {errors.tem_whatsapp_proprio && <p className="text-xs text-red-500">{errors.tem_whatsapp_proprio}</p>}
                            {form.tem_whatsapp_proprio === true && <div className="mt-2"><Input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: formatPhone(e.target.value) })} placeholder="(00) 00000-0000" data-testid="input-whatsapp" className={errors.whatsapp ? 'border-red-500' : ''} />{errors.whatsapp && <p className="text-xs text-red-500 mt-1">{errors.whatsapp}</p>}</div>}
                          </div>
                          <div className="space-y-3 p-4 bg-slate-50 rounded-xl border border-slate-200 mt-4">
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-sm font-semibold text-slate-700">{`${participant.artigo.charAt(0).toUpperCase()}${participant.artigo.slice(1)}`} {participant.nome} possui e-mail próprio?</p>
                              <div className="flex gap-2 flex-shrink-0">
                                <Button type="button" variant={form.tem_email_proprio === true ? 'default' : 'outline'} size="sm" onClick={() => setForm({ ...form, tem_email_proprio: true })} data-testid="btn-email-sim" style={form.tem_email_proprio === true ? btnStyle : undefined}>Sim</Button>
                                <Button type="button" variant={form.tem_email_proprio === false ? 'default' : 'outline'} size="sm" onClick={() => setForm({ ...form, tem_email_proprio: false, email: '' })} data-testid="btn-email-nao" style={form.tem_email_proprio === false ? btnStyle : undefined}>Não</Button>
                              </div>
                            </div>
                            {errors.tem_email_proprio && <p className="text-xs text-red-500">{errors.tem_email_proprio}</p>}
                            {form.tem_email_proprio === true && <div className="mt-2"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@exemplo.com" data-testid="input-email" className={errors.email ? 'border-red-500' : ''} />{errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}</div>}
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {step === 3 && (
                    <div className="space-y-4" data-testid="step-3">
                      <p className="text-sm text-slate-500 mb-2">Marque as opções que se aplicam {participant.artigo === 'o' ? 'ao' : 'à'} {participant.nome}.</p>
                      <div className="flex flex-col gap-3">
                        <div className="flex items-center space-x-2"><Checkbox checked={form.batizado} onCheckedChange={(c) => setForm({ ...form, batizado: !!c })} id="batizado" data-testid="checkbox-batizado" /><Label htmlFor="batizado" className="cursor-pointer">É batizado?</Label></div>
                        <div className="flex items-center space-x-2"><Checkbox checked={form.selado} onCheckedChange={(c) => setForm({ ...form, selado: !!c })} id="selado" data-testid="checkbox-selado" /><Label htmlFor="selado" className="cursor-pointer">É selado?</Label></div>
                        <div className="flex items-center space-x-2"><Checkbox checked={form.auxiliar_jovens} onCheckedChange={(c) => setForm({ ...form, auxiliar_jovens: !!c })} id="axj" /><Label htmlFor="axj" className="cursor-pointer">É auxiliar de jovens?</Label></div>
                        <div className="flex items-center space-x-2"><Checkbox checked={form.grupo_visita} onCheckedChange={(c) => setForm({ ...form, grupo_visita: !!c })} id="gv" /><Label htmlFor="gv" className="cursor-pointer">É integrante de um dos Grupos de Visita?</Label></div>
                        {form.genero === 'feminino' && <div className="flex items-center space-x-2"><Checkbox checked={form.organista} onCheckedChange={(c) => setForm({ ...form, organista: !!c })} id="org" /><Label htmlFor="org" className="cursor-pointer">É organista?</Label></div>}
                        {form.genero === 'feminino' && <div className="flex items-center space-x-2"><Checkbox checked={form.instrutora_musicalizacao} onCheckedChange={(c) => setForm({ ...form, instrutora_musicalizacao: !!c })} id="im" /><Label htmlFor="im" className="cursor-pointer">É instrutora/auxiliar da aula de musicalização?</Label></div>}
                        {form.genero === 'feminino' && <div className="flex items-center space-x-2"><Checkbox checked={form.integrante_ebi} onCheckedChange={(c) => setForm({ ...form, integrante_ebi: !!c })} id="ebi" /><Label htmlFor="ebi" className="cursor-pointer">É integrante do EBI (cuida das crianças)?</Label></div>}
                        {form.genero === 'masculino' && <div className="flex items-center space-x-2"><Checkbox checked={form.instrutor_gem} onCheckedChange={(c) => setForm({ ...form, instrutor_gem: !!c })} id="pub-igem" /><Label htmlFor="pub-igem" className="cursor-pointer">É instrutor de música (GEM)?</Label></div>}
                        {form.genero === 'masculino' && <div className="flex items-center space-x-2"><Checkbox checked={form.aluno_gem} onCheckedChange={(c) => setForm({ ...form, aluno_gem: !!c })} id="pub-agem" /><Label htmlFor="pub-agem" className="cursor-pointer">É aluno da aula de música (GEM)?</Label></div>}
                        {form.genero === 'masculino' && <div className="flex items-center space-x-2"><Checkbox checked={form.musico} onCheckedChange={(c) => setForm({ ...form, musico: !!c })} id="mus" /><Label htmlFor="mus" className="cursor-pointer">É músico?</Label></div>}
                        {form.genero === 'masculino' && form.musico && (
                          <div className="ml-6 p-3 bg-slate-50 rounded-lg border border-slate-200">
                            <p className="text-sm text-slate-600 mb-2">Selecione os instrumentos:</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {INSTRUMENTOS.map((inst) => (
                                <div key={inst} className="flex items-center space-x-2">
                                  <Checkbox checked={toArray(form.instrumentos).includes(inst)} onCheckedChange={() => toggleInstrumento(inst)} id={`pub-inst-${inst}`} /><Label htmlFor={`pub-inst-${inst}`} className="cursor-pointer text-sm">{inst}</Label>
                                </div>
                              ))}
                            </div>
                            {errors.instrumentos && <p className="text-xs text-red-500 mt-2">{errors.instrumentos}</p>}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {step === 4 && (
                    <div className="space-y-4" data-testid="step-4">
                      <p className="text-sm text-slate-500 mb-2">Confira os dados antes de enviar.</p>
                      <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-1">
                        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Dados Pessoais</p>
                        <SummaryRow label="Nome" value={form.nome_completo} />
                        <SummaryRow label="Gênero" value={form.genero === 'masculino' ? 'Masculino' : 'Feminino'} />
                        <SummaryRow label="Nascimento" value={form.data_nascimento ? formatBrazilDate(form.data_nascimento, '') : ''} />
                      </div>
                      <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-1">
                        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Família</p>
                        <SummaryRow label="Pai" value={form.nome_pai} />
                        <SummaryRow label="WhatsApp do Pai" value={form.whatsapp_pai} />
                        <SummaryRow label="Mãe" value={form.nome_mae} />
                        <SummaryRow label="WhatsApp da Mãe" value={form.whatsapp_mae} />
                        <SummaryRow label="Endereço" value={form.endereco_formatado || form.endereco} />
                        <SummaryRow label="Complemento" value={form.complemento} />
                      </div>
                      <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-1">
                        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Contato</p>
                        {form.whatsapp ? <SummaryRow label="WhatsApp" value={form.whatsapp} /> : <SummaryRow label="WhatsApp próprio" value="Não possui" />}
                        {form.email ? <SummaryRow label="E-mail" value={form.email} /> : <SummaryRow label="E-mail próprio" value="Não possui" />}
                      </div>
                      <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-1">
                        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Outros</p>
                        <SummaryRow label="Batizado" value={boolLabel(form.batizado)} />
                        <SummaryRow label="Selado" value={boolLabel(form.selado)} />
                        <SummaryRow label="Auxiliar de jovens" value={boolLabel(form.auxiliar_jovens)} />
                        <SummaryRow label="Grupo de Visita" value={boolLabel(form.grupo_visita)} />
                        {form.genero === 'feminino' && <SummaryRow label="Organista" value={boolLabel(form.organista)} />}
                        {form.genero === 'feminino' && <SummaryRow label="Instrutora musicalização" value={boolLabel(form.instrutora_musicalizacao)} />}
                        {form.genero === 'feminino' && <SummaryRow label="Integrante EBI" value={boolLabel(form.integrante_ebi)} />}
                        {form.genero === 'masculino' && <SummaryRow label="Instrutor GEM" value={boolLabel(form.instrutor_gem)} />}
                        {form.genero === 'masculino' && <SummaryRow label="Aluno GEM" value={boolLabel(form.aluno_gem)} />}
                        {form.genero === 'masculino' && <SummaryRow label="Músico" value={boolLabel(form.musico)} />}
                        {toArray(form.instrumentos).length > 0 && <SummaryRow label="Instrumentos" value={toArray(form.instrumentos).join(', ')} />}
                      </div>
                      {settings.terms_of_use && (
                        <div className="space-y-3 mt-4">
                          <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Termo de Uso</p>
                          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 max-h-40 overflow-y-auto">
                            <p className="text-sm text-slate-600 whitespace-pre-wrap" data-testid="terms-text">{settings.terms_of_use}</p>
                          </div>
                          <div className="flex items-start space-x-2">
                            <Checkbox checked={acceptedTerms} onCheckedChange={(c) => setAcceptedTerms(!!c)} id="terms" data-testid="checkbox-terms" /><Label htmlFor="terms" className="cursor-pointer text-sm leading-tight">Li e aceito o termo de uso</Label>
                          </div>
                          {errors.terms && <p className="text-xs text-red-500">{errors.terms}</p>}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between pt-4 mt-2">
                  <Button type="button" variant="outline" onClick={goBack} data-testid="btn-back" className="gap-1">
                    <ChevronLeft className="w-4 h-4" /> Voltar
                  </Button>
                  {step < WIZARD_STEPS.length - 1 ? (
                    <Button type="button" onClick={goNext} data-testid="btn-next" className="gap-1" style={btnStyle}>
                      Avançar <ChevronRight className="w-4 h-4" />
                    </Button>
                  ) : (
                    <Button type="button" onClick={handleSubmit} disabled={loading} data-testid="submit-registration-btn" style={btnStyle}>
                      {loading ? 'Enviando...' : mode === 'update' ? 'Enviar Atualização' : 'Enviar Cadastro'}
                    </Button>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
        <Footer />
      </div>
    </div>
  );
}

export default function PublicRegistration() {
  const { slug } = useParams();
  return <RegistrationErrorBoundary slug={slug}><PublicRegistrationInner /></RegistrationErrorBoundary>;
}
